import { Column, ColumnDef, Row } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { cn } from "../../../utils/cn";
import {
  PAGE_SIZE,
  PendingStatuses,
  Statuses,
  TABLE_IDS,
  USER_ROLES_IDS,
  VisitorDetails,
} from "../../../constants/common";
import {
  formattedDate,
  formattedDateTimeWithoutSec,
  mapToOptions,
} from "../../../lib/functions";
import { ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import TableWrapper from "../TableWrapper";
import useAuthStore from "../../../store/Auth";
import { useGetPendingApprovedVisitors } from "../../../hooks/dashboard/query";
import { useTranslation } from "react-i18next";
import ComboboxInput from "../../inputs/ComboboxInput";
import { Controller, useForm } from "react-hook-form";
import { useGetAllPendingStatusRoles } from "../../../hooks/common/query";
interface PendingVisitorsTableProps {
  setSelectedPendingApprovalRole: Dispatch<SetStateAction<number>>;
}

const PendingVisitorsTable = ({
  setSelectedPendingApprovalRole,
}: PendingVisitorsTableProps) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const [allVisitors, setAllVisitors] = useState([]);

  const { control, watch } = useForm({
    defaultValues: {
      role: 0,
    },
  });

  const role = watch("role");

  const { data: visitors } = useGetPendingApprovedVisitors({
    type: 2,
    pageNumber: page,
    pageSize: pageSize,
    search: search,
    encryptedLocationID:
      user?.n_RoleID !== USER_ROLES_IDS.ADMIN
        ? user?.n_LocationID?.toString()
          ? user?.n_LocationID?.toString()
          : ""
        : "",
    filterRoleID: role || 0,
  });

  const { data: allRoles } = useGetAllPendingStatusRoles();

  const roles = useMemo(
    () => mapToOptions("s_RoleName", "s_RoleNameAr", "n_RoleID", allRoles),
    [allRoles]
  );

  useEffect(() => {
    if (visitors) {
      setAllVisitors(visitors.data || []);
      setPages(visitors.pagination?.pages || 1);
      setCount(visitors.pagination?.count || 0);
    } else {
      setAllVisitors([]);
    }
  }, [visitors, setPages]);

  const columns: ColumnDef<VisitorDetails>[] = useMemo(
    () =>
      [
        // {
        //   accessorKey: STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS,
        //   header: () => <></>,
        //   cell: ({ row }: { row: Row<VisitorDetails> }) => {
        //     if (row.original.currentRoleAppointmentStatusEN) {
        //       return (
        //         <div
        //           className={cn(
        //             "text-center rounded-md mx-auto w-5 h-5",
        //             row.original.currentRoleAppointmentStatusEN ===
        //               Statuses.APPROVED
        //               ? "bg-green-600"
        //               : row.original.currentRoleAppointmentStatusEN?.split(
        //                   " - "
        //                 )[0] === Statuses.PENDING
        //               ? "bg-yellow-500"
        //               : row.original.currentRoleAppointmentStatusEN ===
        //                 Statuses.REQUEST_INFO
        //               ? "bg-purple-tertiary"
        //               : "bg-red-500"
        //           )}
        //         >
        //           <p className="text-white">
        //             {i18n.language === "en"
        //               ? row.original.currentRoleAppointmentStatusEN.split("")[0]
        //               : row.original.currentRoleAppointmentStatusAR.split(
        //                   ""
        //                 )[0]}
        //           </p>
        //         </div>
        //       );
        //     }
        //   },
        // },
        // {
        //   accessorKey: STATUS_INDICATOR_COLUMN.ADMIN_STATUS,
        //   header: () => <></>,
        //   cell: ({ row }: { row: Row<VisitorDetails> }) => {
        //     return (
        //       <div
        //         className={cn(
        //           "text-center rounded-md mx-auto w-5 h-5",
        //           row.original.overAllAppointmentStatusEN === Statuses.APPROVED
        //             ? "bg-green-600"
        //             : row.original.overAllAppointmentStatusEN ===
        //               Statuses.PENDING
        //             ? "bg-yellow-500"
        //             : row.original.overAllAppointmentStatusEN ===
        //               Statuses.REQUEST_INFO
        //             ? "bg-purple-tertiary"
        //             : "bg-red-500"
        //         )}
        //       >
        //         <p className="text-sm text-white whitespace-normal">
        //           {i18n.language === "en"
        //             ? row.original.overAllAppointmentStatusEN?.split("")[0]
        //             : row.original.overAllAppointmentStatusAR?.split("")[0]}
        //         </p>
        //       </div>
        //     );
        //   },
        // },
        {
          accessorKey: "s_AppointmentCode",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("referenceNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span
                onClick={() => {
                  if (
                    user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                    user?.n_RoleID === USER_ROLES_IDS.RECEPTION
                  ) {
                    if (user?.n_RoleID !== USER_ROLES_IDS.RECEPTION) {
                      navigate(
                        `${
                          APP_ROUTES.APPROVE_REQUEST
                        }?ID=${row?.original?.n_AppointmentID.toString()}`
                      );
                    }
                  }
                }}
                className={cn(
                  (user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
                    user?.n_RoleID === USER_ROLES_IDS.ADMIN) &&
                    "text-blue-800 underline cursor-pointer capitalize"
                )}
              >
                {row.original.s_AppointmentCode}
              </span>
            );
          },
        },
        {
          accessorKey: "s_ApplyFor_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("applyFor")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_ApplyFor_En
                  : row.original.s_ApplyFor_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitorName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("name")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span className="capitalize">{row.original.s_VisitorName}</span>
            );
          },
        },
        {
          accessorKey: "s_Sponsor",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("companyName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Sponsor}</span>;
          },
        },
        {
          accessorKey: "dt_AppointmentStartTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitStartDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentStartTime
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_AppointmentEndTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitEndDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentEndTime
                )}
              </span>
            );
          },
        },
        // {
        //   accessorKey: "currentRoleAppointmentStatusEN",
        //   header: ({ column }: { column: Column<VisitorDetails> }) => (
        //     <Button
        //       className="flex w-full justify-between text-md"
        //       variant="ghost"
        //       onClick={() =>
        //         column.toggleSorting(column.getIsSorted() === "asc")
        //       }
        //     >
        //       {t("currentStatus")}
        //       <ArrowUpDown className="ml-2 h-4 w-4" />
        //     </Button>
        //   ),
        //   cell: ({ row }: { row: Row<VisitorDetails> }) => {
        //     const isPendingWithRole =
        //       row.original.currentRoleAppointmentStatusEN?.split(" - ")[0] ===
        //       Statuses.PENDING;

        //     if (row.original.currentRoleAppointmentStatusEN) {
        //       return (
        //         <div
        //           className={cn(
        //             "text-center rounded-lg py-0.5 mx-auto px-1 w-[135px]",
        //             row.original.currentRoleAppointmentStatusEN ===
        //               Statuses.APPROVED
        //               ? "bg-green-600"
        //               : isPendingWithRole
        //               ? "bg-yellow-500"
        //               : "bg-red-500"
        //           )}
        //         >
        //           <p className="text-white whitespace-break-spaces">
        //             {i18n.language === "en"
        //               ? row.original.currentRoleAppointmentStatusEN
        //               : row.original.currentRoleAppointmentStatusAR}
        //           </p>
        //         </div>
        //       );
        //     }
        //   },
        // },
        {
          accessorKey: "overAllAppointmentStatusEN",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("finalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                  row.original.overAllAppointmentStatusEN === Statuses.APPROVED
                    ? "bg-green-600"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_STAFF
                    ? "bg-yellow-500"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_AUTHORIZED_PERSON
                    ? "bg-orange-light"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_CYBER_SECURITY
                    ? "bg-purple-dark"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY
                    ? "bg-purple-orchid"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY_MANAGER
                    ? "bg-blue-primary"
                    : "bg-red-500",
                  user?.n_RoleID === USER_ROLES_IDS.ADMIN && "cursor-pointer"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {i18n.language === "en"
                    ? row.original.overAllAppointmentStatusEN
                    : row.original.overAllAppointmentStatusAR}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_Email",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("email")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Email}</span>;
          },
        },
        {
          accessorKey: "dt_DateOfBirth",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("dateOfBirth")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_DateOfBirth
                  ? formattedDate(new Date(row.original.dt_DateOfBirth), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Nationality_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationality")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_Nationality_En
                  : row.original.s_Nationality_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_HostName",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostName}</span>;
          },
        },
        {
          accessorKey: "s_HostEmail",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostEmail")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostEmail}</span>;
          },
        },
        {
          accessorKey: "s_HostMobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostMobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
        },
        {
          accessorKey: "s_LocationName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("location")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_LocationName_En
                  : row.original.s_LocationName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_BuildingName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("building")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_BuildingName_En
                  : row.original.s_BuildingName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_GateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("gate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_GateName_En
                  : row.original.s_GateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_MeetingRoomName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("meetingRoom")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_MeetingRoomName_En
                  : row.original.s_MeetingRoomName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DepartmentName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("department")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DepartmentName_En
                  : row.original.s_DepartmentName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_VisitType_En
                  : row.original.s_VisitType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_PurposeEn",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitPurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_PurposeEn
                  : row.original.s_PurposeAr}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DocumentType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("documentType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DocumentType_En
                  : row.original.s_DocumentType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Iqama",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqama")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Iqama}</span>;
          },
        },
        {
          accessorKey: "dt_IqamaExpiry",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqamaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_IqamaExpiry
                  ? formattedDate(new Date(row.original.dt_IqamaExpiry), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "eidNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalID")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.eidNumber}</span>;
          },
        },
        {
          accessorKey: "dt_EIDExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalIDExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_EIDExpiryDate
                  ? formattedDate(new Date(row.original.dt_EIDExpiryDate), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "passportNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.passportNumber}</span>;
          },
        },
        {
          accessorKey: "dt_PassportExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_PassportExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_PassportExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisaNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VisaNo}</span>;
          },
        },
        {
          accessorKey: "dt_VisaExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_VisaExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_VisaExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VehicleNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehicleNo")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VehicleNo || "-"}</span>;
          },
        },
        {
          accessorKey: "s_IsVehicleAllowed_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehiclePermit")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                  row.original.s_VehicleNo
                    ? row.original.n_IsVehicleAllowed === -1
                      ? "bg-yellow-500"
                      : row.original.n_IsVehicleAllowed === 1
                      ? "bg-green-600"
                      : "bg-red-500"
                    : "bg-transparent"
                )}
              >
                <p
                  className={cn(
                    "text-sm whitespace-normal",
                    row.original.s_VehicleNo ? "text-white" : "text-[#828282]"
                  )}
                >
                  {row.original.s_VehicleNo
                    ? i18n.language === "en"
                      ? row.original.s_IsVehicleAllowed_En
                      : row.original.s_IsVehicleAllowed_Ar
                    : "-"}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_CreatedBy",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdBy")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_CreatedBy}</span>;
          },
        },
        {
          accessorKey: "dt_CreatedDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_CreatedDate)}
              </span>
            );
          },
        },
      ].filter((column) => {
        let excludeKeys = [];

        if (user?.n_RoleID !== USER_ROLES_IDS.RECEPTION) {
          excludeKeys.push("action", "s_CheckedInStatus_En");
        }

        if (
          user?.n_RoleID === USER_ROLES_IDS.STAFF ||
          user?.n_RoleID === USER_ROLES_IDS.CYBER_SECURITY
        ) {
          excludeKeys.push("overAllAppointmentStatusEN");
        }
        // if (user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) {
        //   excludeKeys.push(
        //     "currentRoleAppointmentStatusEN",
        //     STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS
        //   );
        // } else {
        //   excludeKeys.push(STATUS_INDICATOR_COLUMN.ADMIN_STATUS);
        // }

        return !excludeKeys.includes(column.accessorKey);
      }),
    [i18n.language, navigate, t, user?.n_RoleID]
  );
  return (
    <TableWrapper
      data={allVisitors}
      columns={columns}
      search={search}
      setSearch={setSearch}
      page={page}
      setPage={setPage}
      totalPages={pages}
      count={count}
      isSearch
      tableId={TABLE_IDS.PENDING_APPROVALS}
      ButtonsWrapper={
        <Controller
          name="role"
          control={control}
          render={({ field: { onChange, name, value } }) => {
            return (
              <ComboboxInput
                options={roles}
                value={
                  roles?.find((item) => item.value === value)?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  setSelectedPendingApprovalRole(Number(value));
                }}
                styles="py-2 mb-1.5 rounded-lg ring-1 ring-purple-primary dark:ring-white focus:outline-none focus:ring-1"
              />
            );
          }}
        />
      }
      isHideStatusIndicator
      hideColumns={{
        s_ApplyFor_En: false,
        s_HostEmail: false,
        eidNumber: false,
        dt_EIDExpiryDate: false,
        passportNumber: false,
        dt_PassportExpiryDate: false,
        s_VisaNo: false,
        dt_VisaExpiryDate: false,
        s_CreatedBy: false,
        dt_CreatedDate: false,
      }}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};

export default React.memo(PendingVisitorsTable);
